import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  FileSearch,
  Search,
  Settings,
  XIcon,
  Eye,
  ThumbsUp,
  ThumbsDown,
  Star,
  Download,
  ChevronDown,
  GalleryVerticalEnd,
  GripVertical,
  Loader2,
} from "lucide-react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  horizontalListSortingStrategy,
} from "@dnd-kit/sortable";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import usePaginationStore from "@/hooks/stores/usePaginationStore";

// Sortable Table Header Component
const SortableTableHeader = ({
  column,
  index,
  sortConfig,
  handleSort,
  visibleColumns,
  selectedApplications,
  filteredApplications,
  handleSelectAll,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: column.key,
    disabled: column.key === "actions", // Disable dragging for actions column
  });

  const style = {
    transform: CSS.Transform.toString({ ...transform, scaleX: 1, scaleY: 1 }),

    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  // Special handling for actions column (not draggable, always last)
  if (column.key === "actions") {
    return (
      <th
        ref={setNodeRef}
        style={style}
        className="px-4 pt-4 pb-2 text-foreground align-top relative"
      >
        <div className="mb-[10px] flex items-center justify-center">
          <p className="mr-1 text-base font-medium text-muted-foreground dark:text-muted-foreground">
            {column.label}
          </p>
        </div>
      </th>
    );
  }

  // Special handling for selection column
  if (column.key === "selection") {
    return (
      <th
        ref={setNodeRef}
        style={style}
        className={`px-4 py-4 text-foreground align-center relative`}
      // {...attributes}
      // {...listeners}
      >
        <div className="flex items-center justify-center">
          <input
            type="checkbox"
            checked={
              selectedApplications.length === filteredApplications.length &&
              filteredApplications.length > 0
            }
            onChange={(e) => handleSelectAll(e)}
            className="form-checkbox h-4 w-4 text-primary transition duration-150 ease-in-out"
          />
        </div>
      </th>
    );
  }

  return (
    <th
      ref={setNodeRef}
      style={style}
      className={`px-4 pt-4 pb-2 text-foreground align-top relative`}
      {...attributes}
      {...listeners}
    >
      <div className="flex items-center justify-between relative group">
        {column.sortable ? (
          <div className="flex-1 pb-[10px]">
            <div
              className=" flex items-center cursor-pointer"
              onClick={() => handleSort(column.key)}
            >
              <p className="mr-1 text-base font-medium text-muted-foreground dark:text-muted-foreground  whitespace-nowrap">
                {column.label}
              </p>
              <div className="inline-flex flex-col space-y-[2px] text-muted-foreground dark:text-muted-foreground">
                {sortConfig.key === column.key ? (
                  sortConfig.direction === "ascending" ? (
                    <span className="inline-block cursor-pointer text-white">
                      <svg width="10" height="5" viewBox="0 0 10 5" fill="none">
                        <path d="M5 0L0 5H10L5 0Z" fill="currentColor" />
                      </svg>
                    </span>
                  ) : (
                    <span className="inline-block cursor-pointer text-muted-foreground">
                      <svg width="10" height="5" viewBox="0 0 10 5" fill="none">
                        <path
                          d="M5 5L10 0L-4.37114e-07 8.74228e-07L5 5Z"
                          fill="currentColor"
                        />
                      </svg>
                    </span>
                  )
                ) : column.key === "name" ? (
                  <>
                    <span
                      className="inline-block cursor-pointer text-foreground"
                      onClick={() => {
                        handleSort(column.key);
                      }}
                    >
                      <svg width="10" height="5" viewBox="0 0 10 5" fill="none">
                        <path d="M5 0L0 5H10L5 0Z" fill="currentColor" />
                      </svg>
                    </span>
                    <span
                      className="inline-block cursor-pointer text-foreground"
                      onClick={() => {
                        handleSort(column.key);
                      }}
                    >
                      <svg width="10" height="5" viewBox="0 0 10 5" fill="none">
                        <path
                          d="M5 5L10 0L-4.37114e-07 8.74228e-07L5 5Z"
                          fill="currentColor"
                        />
                      </svg>
                    </span>
                  </>
                ) : (
                  <>
                    <span
                      className="inline-block cursor-pointer text-foreground"
                      onClick={() => {
                        handleSort(column.key);
                      }}
                    >
                      <svg width="10" height="5" viewBox="0 0 10 5" fill="none">
                        <path d="M5 0L0 5H10L5 0Z" fill="currentColor" />
                      </svg>
                    </span>
                    <span
                      className="inline-block cursor-pointer text-foreground"
                      onClick={() => {
                        handleSort(column.key);
                      }}
                    >
                      <svg width="10" height="5" viewBox="0 0 10 5" fill="none">
                        <path
                          d="M5 5L10 0L-4.37114e-07 8.74228e-07L5 5Z"
                          fill="currentColor"
                        />
                      </svg>
                    </span>
                  </>
                )}
              </div>
            </div>
          </div>
        ) : (
          <div className="mb-[10px] flex items-center justify-center flex-1">
            <p className="mr-1 text-base font-medium text-muted-foreground dark:text-muted-foreground">
              {column.label}
            </p>
          </div>
        )}

        {/* Drag Handle - only show for draggable columns */}
        {/* {column.key !== "selection" && (
          <div className="flex-1 pb-[10px] group-hover:block hidden">
            <GripVertical className="h-4 w-4 text-white" />
            </div>
        )} */}
      </div>
    </th>
  );
};

import { useApplicationsByVacancyId } from "../../hooks/queries/useApplication";
import Loader from "../Loader";
import { useUpdateApplicationStatus } from "../../hooks/mutations/applications";
import useToastStore from "@/components/Common/Toast/toastStore";
import ApplicationFilters from "./ApplicationFilters";
import Pagination from "../Common/Pagination";
import ApplicationView from "@/components/applications/ApplicationView";
import ApplicationViewModal from "@/components/applications/ApplicationViewModal";
import { useDownloadCVs } from "@/hooks/mutations/useDownloadCVs";
import { useDebounce } from "@/utils/helpers";
// import { PER_PAGE } from "@/config";
import DialogBox from "../Common/DialogBox";
import { Editor } from "@tinymce/tinymce-react";
import { emailService } from "@/services/emailService";
import { applicationService } from "@/services/applicationService";
import Dropdown from "@/components/ui/Dropdown";

/** Format custom field values stored as JSON arrays/strings for display */
const formatFieldDisplayValue = (value) => {
  if (value === null || value === undefined || value === "") return "";
  if (Array.isArray(value)) {
    return value.filter((v) => v !== null && v !== undefined && v !== "").map(String).join(", ");
  }
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (
      (trimmed.startsWith("[") && trimmed.endsWith("]")) ||
      (trimmed.startsWith("{") && trimmed.endsWith("}"))
    ) {
      try {
        const parsed = JSON.parse(trimmed);
        if (Array.isArray(parsed)) {
          return parsed
            .filter((v) => v !== null && v !== undefined && v !== "")
            .map(String)
            .join(", ");
        }
        if (parsed && typeof parsed === "object") {
          return Object.values(parsed)
            .filter((v) => v !== null && v !== undefined && v !== "")
            .map(String)
            .join(", ");
        }
        return parsed == null ? "" : String(parsed);
      } catch {
        return value;
      }
    }
    return value;
  }
  return String(value);
};

const ApplicationTable = ({ selectedVacancy, initialFilters }) => {
  const { getPerPage, setPerPage } = usePaginationStore();
  const [filteredApplications, setFilteredApplications] = useState([]);
  const [currentPage, setCurrentPage] = useState(() => {
    const saved = localStorage.getItem("currentPage");
    return saved ? parseInt(saved) : 1;
  });
  const [searchTerm, setSearchTerm] = useState(() => {
    const saved = localStorage.getItem("searchTerm");
    return saved || "";
  });
  const [sortConfig, setSortConfig] = useState(() => {
    const saved = localStorage.getItem("sortConfig");
    if (saved) {
      const parsed = JSON.parse(saved);
      if (parsed?.key === "created_at") {
        parsed.key = "updated_at";
      }
      return parsed;
    }
    return {
      key: "priority",
      direction: "descending",
    };
  });

  const { showToast } = useToastStore;
  const [updateAll, setUpdateAll] = useState(false);

  const filtersStorageKey = `filters_${selectedVacancy?.id || "vacancy"}`;
  const activeFiltersStorageKey = `activeFilters_${selectedVacancy?.id || "vacancy"
    }`;
  const visibleColumnsStorageKey = `visibleColumns_${selectedVacancy?.id || "vacancy"
    }`;
  const sortConfigStorageKey = `sortConfig_${selectedVacancy?.id || "vacancy"}`;

  const [filters, setFilters] = useState(() => {
    const saved = localStorage.getItem(filtersStorageKey);
    if (!saved) return {};
    const parsed = JSON.parse(saved);
    if (parsed.created_at && !parsed.updated_at) {
      parsed.updated_at = parsed.created_at;
      delete parsed.created_at;
    }
    return parsed;
  });
  const [activeFilters, setActiveFilters] = useState(() => {
    const saved = localStorage.getItem(activeFiltersStorageKey);
    if (!saved) return [];
    return JSON.parse(saved).map((key) =>
      key === "created_at" ? "updated_at" : key,
    );
  });
  const [customFields, setCustomFields] = useState(() => {
    const personalFields = selectedVacancy?.custom_fields?.personal || [];
    const jobFields = selectedVacancy?.custom_fields?.job || [];

    console.log("Personal fields:", personalFields);
    console.log("Job fields:", jobFields);

    // Combine both personal and job fields
    const combinedFields = [...personalFields, ...jobFields];
    console.log("Combined fields:", combinedFields);

    return combinedFields;
  });
  const defaultVisibleColumns = {
    name: true,
    email: true,
    stage: true,
    rating: true,
    sms_score: true,
    updated_at: true,
    priority: true,
    actions: true,
    selection: true,
    ...(customFields?.length > 0
      ? customFields.reduce((acc, field) => {
        if (
          !["heading", "divider", "file", "spacer"].includes(field.field_type)
        ) {
          acc[`field_${field.label}`] = false;
        }
        return acc;
      }, {})
      : {}),
  };
  const [visibleColumns, setVisibleColumns] = useState(() => {
    const saved = localStorage.getItem(visibleColumnsStorageKey);
    return saved ? JSON.parse(saved) : defaultVisibleColumns;
  });
  const [showColumnToggle, setShowColumnToggle] = useState(false);
  const [showFilterToggle, setShowFilterToggle] = useState(false);
  const [selectedApplications, setSelectedApplications] = useState(() => {
    const saved = localStorage.getItem("selectedApplications");
    return saved ? JSON.parse(saved) : [];
  });
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const statusDropdownRef = useRef(null);
  const navigate = useNavigate();
  const { id: companyId } = useParams();
  const [itemsPerPage, setItemsPerPage] = useState(
    getPerPage("applications-table"),
  );
  const columnToggleRef = useRef(null);
  const filterToggleRef = useRef(null);
  const [isAnyCheckboxChecked, setIsAnyCheckboxChecked] = useState(false);
  const [showVacanciesDropdown, setShowVacanciesDropdown] = useState(false);
  const vacanciesDropdownRef = useRef(null);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [serverPagination, setServerPagination] = useState({
    current_page: 1,
    last_page: 1,
    total: 0,
    per_page: itemsPerPage,
  });

  const {
    data: applications,
    refetch: refetchApplications,
    isLoading: isApplicationsLoading,
  } = useApplicationsByVacancyId(selectedVacancy?.id, {
    per_page: serverPagination.per_page,
    page: serverPagination.current_page,
    search: searchQuery,
    customFilters: filters,
    // include sorting info for server-side sorting and cache key
    sortBy: sortConfig?.key || undefined,
    sortOrder:
      sortConfig?.direction === "ascending"
        ? "asc"
        : sortConfig?.direction === "descending"
          ? "desc"
          : undefined,
    queryKey: [
      "applications-table",
      selectedVacancy?.id,
      serverPagination.current_page,
      serverPagination.per_page,
      searchQuery,
      filters,
      {
        sort_by: sortConfig?.key || null,
        sort_order:
          sortConfig?.direction === "ascending"
            ? "asc"
            : sortConfig?.direction === "descending"
              ? "desc"
              : null,
      },
    ],
  });

  const { mutate: updateApplicationStatus, isPending: isUpdatingStatus } =
    useUpdateApplicationStatus();

  const [processedApplications, setProcessedApplications] = useState([]);

  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState(null);

  const [showCVPopup, setShowCVPopup] = useState(false);
  const [cvUrl, setCvUrl] = useState("");
  const [cvCandidateName, setCvCandidateName] = useState("");
  const [cvApplicationId, setCvApplicationId] = useState(null);
  const cvPopupRef = useRef(null);

  const [showDownloadDropdown, setShowDownloadDropdown] = useState(false);
  const [showDownloadCVDialog, setShowDownloadCVDialog] = useState(false);
  const downloadDropdownRef = useRef(null);

  // function to handle search term input
  const updateSearchTerm = (term) => {
    setSearchQuery(term);
    setServerPagination((prev) => ({ ...prev, current_page: 1 }));
  };

  // defined useDebounce for search term input
  const debouncedSearch = useDebounce(updateSearchTerm, 1000);

  const [tempSearchTerm, setTempSearchTerm] = useState("");
  const handleSearchChange = (term) => {
    setTempSearchTerm(term);
    debouncedSearch(term);
  };

  useEffect(() => {
    if (selectedVacancy) {
      const personalFields = selectedVacancy?.custom_fields?.personal || [];
      const jobFields = selectedVacancy?.custom_fields?.job || [];

      console.log("useEffect - Personal fields:", personalFields);
      console.log("useEffect - Job fields:", jobFields);

      // Combine both personal and job fields
      const combinedFields = [...personalFields, ...jobFields];
      console.log("useEffect - Combined fields:", combinedFields);

      setCustomFields(combinedFields);
      // Load filters from local storage instead of resetting them
      const filtersKey = `filters_${selectedVacancy?.id || "vacancy"}`;
      const activeFiltersKey = `activeFilters_${selectedVacancy?.id || "vacancy"}`;
      const visibleColumnsKey = `visibleColumns_${selectedVacancy?.id || "vacancy"}`;
      const sortConfigKey = `sortConfig_${selectedVacancy?.id || "vacancy"}`;

      const savedFilters = localStorage.getItem(filtersKey);
      const savedActiveFilters = localStorage.getItem(activeFiltersKey);
      const savedVisibleColumns = localStorage.getItem(visibleColumnsKey);
      const savedSortConfig = localStorage.getItem(sortConfigKey);

      setFilters({});
      setActiveFilters([]);
      setVisibleColumns(defaultVisibleColumns);
      setSortConfig({});
      if (savedFilters) {
        const parsedFilters = JSON.parse(savedFilters);
        // Migrate legacy created_at filter key to updated_at
        if (parsedFilters.created_at && !parsedFilters.updated_at) {
          parsedFilters.updated_at = parsedFilters.created_at;
          delete parsedFilters.created_at;
        }
        setFilters(parsedFilters);
      }
      if (savedActiveFilters) {
        const parsedActiveFilters = JSON.parse(savedActiveFilters).map(
          (key) => (key === "created_at" ? "updated_at" : key),
        );
        setActiveFilters(parsedActiveFilters);
      }
      // Clear old visible columns data to use new defaults
      localStorage.removeItem(visibleColumnsKey);
      setVisibleColumns(defaultVisibleColumns);
      if (savedSortConfig) {
        const parsedSortConfig = JSON.parse(savedSortConfig);
        if (parsedSortConfig?.key === "created_at") {
          parsedSortConfig.key = "updated_at";
        }
        setSortConfig(parsedSortConfig);
      }

      setIsLoading(true);
    }
  }, [selectedVacancy]);

  useEffect(() => {
    // console.log("Pagination data:", applications?.pagination);
    setServerPagination((prev) => ({
      ...prev,
      ...(applications?.pagination ?? {}),
    }));
    if (applications && Array.isArray(applications.applications)) {
      const enrichedApplications = applications.applications.map(
        (application) => {
          const candidate = application.candidate || {};
          const vacancy = selectedVacancy;

          // const resume = application.media?.find(
          //   (m) => m.collection_name === "documents"
          // );
          // const resumeUrl = resume?.original_url || "";
          const resume = application.resume || "";

          const fieldsData = {};
          if (
            application.application_fields &&
            Array.isArray(application.application_fields)
          ) {
            application.application_fields.forEach((field) => {
              fieldsData[field.field_name] = field.field_value;
            });
          }

          const dateToDisplay = new Date(
            application.updated_at || application.created_at,
          );

          const formattedDateTime = dateToDisplay.toLocaleString("en-US", {
            year: "numeric",
            month: "numeric",
            day: "numeric",
            hour: "2-digit",
            minute: "2-digit",
            hour12: true,
          });

          // Get last status
          let lastStatus = "";
          const statusHistory = application.application_status_history || [];
          if (
            application.status?.status?.name === "Rejected" &&
            statusHistory.length > 1
          ) {
            const sortedHistory = statusHistory.sort(
              (a, b) => new Date(b.changed_at) - new Date(a.changed_at),
            );
            const previousStatus = sortedHistory.find(
              (status) => status.vacancy_status?.status?.name !== "Rejected",
            );
            lastStatus = previousStatus?.vacancy_status?.status?.name || "";
          }
          return {
            id: application.id,
            // candidate.name already includes full name; do not append last_name again
            candidateName: candidate?.name || "Unknown",
            vacancyTitle: vacancy?.title || "Unknown Position",
            // resumeUrl: resumeUrl,
            rating: application?.total_score.total_score || 0,
            sms_score: Number(application?.sms_score) || 0,
            currentStage: application.status?.status?.name || "",
            lastStatus: lastStatus,
            priority: application.priority || 0,
            email: candidate?.email || "",
            resume: resume,
            vacancy_id: application.vacancy_id,
            candidate_id: application.candidate_id,
            appliedTime: dateToDisplay.toLocaleTimeString(),
            appliedDateTime: formattedDateTime,
            rawDateTime: dateToDisplay.getTime(),
            updated_at: application.updated_at || application.created_at,
            fields: fieldsData,
            raw_application: application,
            tags: application?.application_tags || [],
          };
        },
      );

      setProcessedApplications(enrichedApplications);
      setFilteredApplications(enrichedApplications);

      // Log the processed applications
      // console.log("Processed Applications:", enrichedApplications);

      // Log SMS scores
      console.log(
        "SMS Scores:",
        enrichedApplications.map((app) => ({
          id: app.id,
          name: app.candidateName,
          sms_score: app.sms_score,
        })),
      );
    } else {
      setProcessedApplications([]);
      setFilteredApplications([]);
    }
    setIsLoading(false);
  }, [applications]);

  useEffect(() => {
    setServerPagination((prev) => ({ ...prev, current_page: 1 }));
    localStorage.setItem(filtersStorageKey, JSON.stringify(filters));
    // console.log("filters", filters);
  }, [filters, filtersStorageKey]);

  useEffect(() => {
    localStorage.setItem(
      activeFiltersStorageKey,
      JSON.stringify(activeFilters),
    );
  }, [activeFilters, activeFiltersStorageKey]);

  useEffect(() => {
    if (processedApplications.length > 0) {
      let result = [...processedApplications];

      // Apply sorting
      if (sortConfig.key) {
        result.sort((a, b) => {
          let aValue, bValue;

          // Handle different column types
          switch (sortConfig.key) {
            case "name":
              aValue = a.candidateName?.toLowerCase() || "";
              bValue = b.candidateName?.toLowerCase() || "";
              break;
            case "stage":
              aValue = a.currentStage?.toLowerCase() || "";
              bValue = b.currentStage?.toLowerCase() || "";
              break;
            case "rejected_at":
              aValue = a.lastStatus?.toLowerCase() || "";
              bValue = b.lastStatus?.toLowerCase() || "";
              break;
            case "rating":
              aValue = Number(a.rating) || 0;
              bValue = Number(b.rating) || 0;
              break;
            case "sms_score":
              aValue = Number(a.sms_score) || 0;
              bValue = Number(b.sms_score) || 0;
              break;
            case "updated_at":
              aValue = new Date(a.rawDateTime).getTime();
              bValue = new Date(b.rawDateTime).getTime();
              break;
            case "priority":
              aValue = Number(a.priority) || 0;
              bValue = Number(b.priority) || 0;
              break;
            case "tags":
              aValue =
                a.tags
                  ?.map((tag) => tag.name)
                  .join(",")
                  .toLowerCase() || "";
              bValue =
                b.tags
                  ?.map((tag) => tag.name)
                  .join(",")
                  .toLowerCase() || "";
              break;
            default:
              // Check if this is a custom field by looking for it in the fields object
              if (a.fields && b.fields && sortConfig.key in a.fields) {
                aValue = String(a.fields[sortConfig.key] || "").toLowerCase();
                bValue = String(b.fields[sortConfig.key] || "").toLowerCase();
              } else if (sortConfig.key.startsWith("field_")) {
                const fieldName = sortConfig.key.replace("field_", "");
                aValue = String(a.fields?.[fieldName] || "").toLowerCase();
                bValue = String(b.fields?.[fieldName] || "").toLowerCase();
              } else {
                aValue = String(a[sortConfig.key] || "").toLowerCase();
                bValue = String(b[sortConfig.key] || "").toLowerCase();
              }
          }

          // Compare values
          if (aValue < bValue) {
            return sortConfig.direction === "ascending" ? -1 : 1;
          }
          if (aValue > bValue) {
            return sortConfig.direction === "ascending" ? 1 : -1;
          }
          return 0;
        });
      }

      setFilteredApplications(result);
    } else {
      setFilteredApplications([]);
    }
  }, [filters, activeFilters, processedApplications, sortConfig]);

  useEffect(() => {
    localStorage.setItem(
      visibleColumnsStorageKey,
      JSON.stringify(visibleColumns),
    );
  }, [visibleColumns, visibleColumnsStorageKey]);

  const handleSort = (key) => {
    let direction = "ascending";
    if (sortConfig.key === key && sortConfig.direction === "ascending") {
      direction = "descending";
    }

    const newSortConfig = { key, direction };
    setSortConfig(newSortConfig);
    localStorage.setItem(sortConfigStorageKey, JSON.stringify(newSortConfig));
    // reset to first page when sorting changes so server fetch aligns
    setServerPagination((prev) => ({ ...prev, current_page: 1 }));
  };

  const handleFilterChange = (field, value) => {
    setFilters((prev) => {
      const newFilters = {
        ...prev,
        [field]: value,
      };

      localStorage.setItem(filtersStorageKey, JSON.stringify(newFilters));

      return newFilters;
    });
  };

  const addFilter = (filterKey) => {
    if (!activeFilters.includes(filterKey)) {
      const newActiveFilters = [...activeFilters, filterKey];
      setActiveFilters(newActiveFilters);
      localStorage.setItem(
        activeFiltersStorageKey,
        JSON.stringify(newActiveFilters),
      );
    }
  };

  const removeFilter = (filterKey) => {
    const newActiveFilters = activeFilters.filter((key) => key !== filterKey);
    setActiveFilters(newActiveFilters);
    localStorage.setItem(
      activeFiltersStorageKey,
      JSON.stringify(newActiveFilters),
    );

    setFilters((prev) => {
      const newFilters = { ...prev };
      delete newFilters[filterKey];
      localStorage.setItem(filtersStorageKey, JSON.stringify(newFilters));
      return newFilters;
    });
  };

  const getStatusColor = (status) => {
    switch (status.toLowerCase()) {
      case "in review":
        return "text-yellow-600 bg-yellow-100";
      case "shortlisted":
        return "text-green-600 bg-green-100";
      case "rejected":
        return "text-red-600 bg-red-100";
      case "hired":
        return "text-blue-600 bg-blue-100";
      default:
        return "text-muted-foreground bg-muted";
    }
  };

  const getRatingColor = (rating) => {
    if (rating >= 4) return "text-green-600";
    if (rating >= 3) return "text-yellow-600";
    return "text-red-600";
  };

  const getSmsScoreColor = (smsScore) => {
    if (smsScore >= 70) return "text-green-600";
    if (smsScore >= 50) return "text-yellow-600";
    return "text-red-600";
  };

  const compareVacancyStatusOrder = (a, b) => {
    const orderA = parseInt(a.p_order, 10);
    const orderB = parseInt(b.p_order, 10);
    return (
      (Number.isNaN(orderA) ? 999 : orderA) -
      (Number.isNaN(orderB) ? 999 : orderB)
    );
  };

  const columns = React.useMemo(
    () => [
      {
        key: "selection",
        label: "Selection",
        sortable: false,
        filterable: false,
      },
      {
        key: "name",
        label: "Name",
        sortable: true,
        filterable: true,
        inputType: "text",
        searchIcon: true,
      },
      {
        key: "email",
        label: "Email",
        sortable: true,
        filterable: true,
        inputType: "text",
        searchIcon: true,
      },
      {
        key: "stage",
        label: "Stage",
        sortable: true,
        filterable: true,
        inputType: "select",
        arrowIcon: true,
        useMultiSelect: true,
        options: [...(selectedVacancy?.vacancy_statuses || [])]
          .sort(compareVacancyStatusOrder)
          .map((status) => ({
            value: status.status.id,
            label: status.status.name,
          })),
      },
      {
        key: "rejected_at",
        label: "Rejected At",
        sortable: true,
        filterable: true,
        inputType: "select",
        arrowIcon: true,
        useMultiSelect: true,
        options: [...(selectedVacancy?.vacancy_statuses || [])]
          .sort(compareVacancyStatusOrder)
          .map((status) => ({
            value: status.status.id,
            label: status.status.name,
          })),
      },
      {
        key: "rating",
        label: "Rating",
        sortable: true,
        filterable: true,
        inputType: "number",
        arrowIcon: true,
      },
      {
        key: "sms_score",
        label: "Social Mobility Score",
        sortable: true,
        filterable: true,
        inputType: "number",
        arrowIcon: true,
      },
      {
        key: "updated_at",
        label: "Date Last Modified",
        sortable: true,
        filterable: true,
        inputType: "datetime",
        calendarIcon: true,
      },
      ...(customFields.length > 0
        ? customFields
          ?.map((_field) => {
            if (
              [
                "heading",
                "divider",
                "file",
                "spacer",
                "university_repeatable",
              ].includes(_field.field_type)
            ) {
              return null;
            } else {
              return {
                key: `${_field.label}`,
                label: _field.label,
                sortable: true,
                filterable: true,
                inputType: _field.field_type,
                isField: true,
                useMultiSelect: ["select", "checkbox", "radio"].includes(
                  _field.field_type,
                ),
                options: _field.options
                  ? Array.isArray(_field.options)
                    ? _field.options
                    : _field.options.split(",")
                  : [],
              };
            }
          })
          .filter(Boolean)
        : []),

      // add priortiy column
      {
        key: "priority",
        label: "Priority",
        filterable: true,
        sortable: true,
        inputType: "number",
      },
      {
        key: "tags",
        label: "Tags",
        sortable: true,
        filterable: true,
        inputType: "text",
        // calendarIcon: true,
      },
      {
        key: "actions",
        label: "Actions",
        sortable: false,
        filterable: false,
      },
    ],
    [selectedVacancy?.id, customFields],
  );

  const toggleColumnVisibility = (columnKey) => {
    setVisibleColumns((prev) => {
      const updatedColumns = {
        ...prev,
        [columnKey]: !prev[columnKey],
      };
      const newVisibleColumns = Object.fromEntries(
        Object.entries(updatedColumns).filter(([key]) =>
          columns.some((column) => column.key === key),
        ),
      );
      localStorage.setItem(
        visibleColumnsStorageKey,
        JSON.stringify(newVisibleColumns),
      );

      if (!newVisibleColumns[columnKey]) {
        if (activeFilters.includes(columnKey)) {
          removeFilter(columnKey);
        }

        const fieldPrefix = `field_`;
        if (columnKey.startsWith(fieldPrefix)) {
          const relatedFilters = activeFilters.filter(
            (filter) =>
              filter === columnKey ||
              (filter.startsWith(fieldPrefix) &&
                filter.includes(columnKey.substring(fieldPrefix.length))),
          );
          relatedFilters.forEach((filter) => removeFilter(filter));
        }
      }
      return newVisibleColumns;
    });
  };

  const [columnOrder, setColumnOrder] = useState([]);

  const visibleColumnsArray = React.useMemo(() => {
    if (!columns || columns.length === 0) {
      return [];
    }

    const allVisibleColumns = columns.filter(
      (column) => visibleColumns[column.key],
    );

    // Separate actions column to always show last
    const actionsColumn = allVisibleColumns.find(
      (col) => col.key === "actions",
    );
    const otherColumns = allVisibleColumns.filter(
      (col) => col.key !== "actions",
    );

    if (!columnOrder || columnOrder.length === 0) {
      return [...otherColumns, ...(actionsColumn ? [actionsColumn] : [])];
    }

    // Create ordered array based on columnOrder (excluding actions)
    const orderedColumns = [];

    // First, add columns in the saved order (only if they're visible and exist, excluding actions)
    columnOrder.forEach((key) => {
      if (key !== "actions") {
        const column = columns.find((col) => col.key === key);
        if (column && visibleColumns[column.key]) {
          orderedColumns.push(column);
        }
      }
    });

    // Then add any remaining visible columns that aren't in the order (excluding actions)
    otherColumns.forEach((column) => {
      if (!orderedColumns.some((col) => col.key === column.key)) {
        orderedColumns.push(column);
      }
    });

    // Always add actions column at the end if visible
    return [...orderedColumns, ...(actionsColumn ? [actionsColumn] : [])];
  }, [columns, visibleColumns, columnOrder]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        columnToggleRef.current &&
        !columnToggleRef.current.contains(event.target)
      ) {
        setShowColumnToggle(false);
      }
      if (
        filterToggleRef.current &&
        !filterToggleRef.current.contains(event.target)
      ) {
        setShowFilterToggle(false);
      }
      if (
        statusDropdownRef.current &&
        !statusDropdownRef.current.contains(event.target)
      ) {
        setShowStatusDropdown(false);
      }
      if (
        vacanciesDropdownRef.current &&
        !vacanciesDropdownRef.current.contains(event.target)
      ) {
        setShowVacanciesDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const allIds = filteredApplications.map((app) => app.id);
      setSelectedApplications(allIds);
      setIsAnyCheckboxChecked(true);
      localStorage.setItem("selectedApplications", JSON.stringify(allIds));
    } else {
      setSelectedApplications([]);
      setIsAnyCheckboxChecked(false);
      localStorage.setItem("selectedApplications", JSON.stringify([]));
    }
  };

  const handleSelectApplication = (id) => {
    setSelectedApplications((prev) => {
      const newSelected = prev.includes(id)
        ? prev.filter((appId) => appId !== id)
        : [...prev, id];
      setIsAnyCheckboxChecked(newSelected.length > 0);
      localStorage.setItem("selectedApplications", JSON.stringify(newSelected));
      return newSelected;
    });
  };

  const getApplicationTableCellClass = (column) => {
    switch (column.key) {
      case "selection":
        return "px-4 py-4 whitespace-nowrap text-center";
      case "tags":
        return "px-6 py-4";
      case "updated_at":
      case "priority":
        return "px-4 py-4 whitespace-nowrap text-sm";
      default:
        if (column.isField) {
          return "px-4 py-4 whitespace-nowrap";
        }
        return "px-4 py-4 whitespace-nowrap";
    }
  };

  const renderApplicationCellInner = (column, application) => {
    switch (column.key) {
      case "selection":
        return (
          <input
            type="checkbox"
            checked={selectedApplications.includes(application.id)}
            onChange={() => handleSelectApplication(application.id)}
            className="form-checkbox h-4 w-4 text-primary transition duration-150 ease-in-out"
          />
        );
      case "name":
        return (
          <div className="text-sm font-medium text-foreground">
            <a
              href={`/vacancies/${application.vacancy_id}/applications/${application.id}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary-dark hover:text-primary-dark font-medium transition-colors px-3 py-1 rounded-md inline-flex items-center gap-1 max-sm:px-0"
            >
              {application.candidateName}
              {application.priority > 0 && (
                <Star
                  className="w-3 h-3 text-yellow-400"
                  fill="currentColor"
                />
              )}
            </a>
          </div>
        );
      case "email":
        return (
          <div className="text-sm font-medium text-foreground break-all">
            {application.email}
          </div>
        );
      case "stage":
        return application.currentStage ? (
          <span
            className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(
              application.currentStage,
            )}`}
          >
            {application.currentStage}
          </span>
        ) : (
          <span>{application.currentStage}</span>
        );
      case "rejected_at":
        return application.currentStage === "Rejected" &&
          application.lastStatus ? (
          <span
            className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(
              application.lastStatus,
            )}`}
          >
            {application.lastStatus}
          </span>
        ) : (
          <span className="text-muted-foreground text-4xl">-</span>
        );
      case "rating":
        return (
          <span
            className={`text-sm font-medium ${getRatingColor(
              application.rating,
            )}`}
          >
            {application.rating} / 10
          </span>
        );
      case "sms_score":
        return (
          <span
            className={`text-sm font-medium ${getSmsScoreColor(
              application.sms_score,
            )}`}
          >
            {application.sms_score !== null && application.sms_score !== undefined
              ? `${application.sms_score}%`
              : "0%"}
          </span>
        );
      case "updated_at":
        return application.appliedDateTime;
      case "priority":
        return application?.priority || 0;
      case "tags":
        return (
          <div className="flex flex-wrap gap-1 justify-end md:justify-start">
            {application?.tags && application?.tags.length > 0 ? (
              application.tags.map((tag) => (
                <span
                  key={tag.id}
                  className="px-2 py-1 text-xs font-medium bg-indigo-100 text-indigo-800 rounded-full"
                >
                  {tag?.name}
                </span>
              ))
            ) : (
              <span className="text-sm">No tags</span>
            )}
          </div>
        );
      case "actions":
        return (
          <div className="flex items-center justify-center gap-2">
            <a
              href={`/vacancies/${application.vacancy_id}/applications/${application.id}`}
              target="_blank"
              rel="noopener noreferrer"
              title="View application"
              className="text-primary-dark hover:opacity-80 transition-opacity p-1 rounded-md inline-flex items-center"
            >
              <Eye className="h-4 w-4" />
            </a>
          </div>
        );
      default:
        if (column.isField) {
          return (
            <div className="text-sm text-foreground">
              {formatFieldDisplayValue(application.fields?.[column.key])}
            </div>
          );
        }
        return null;
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        statusDropdownRef.current &&
        !statusDropdownRef.current.contains(event.target)
      ) {
        setShowStatusDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const [statusOptions, setStatusOptions] = useState([]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        vacanciesDropdownRef.current &&
        !vacanciesDropdownRef.current.contains(event.target)
      ) {
        setShowVacanciesDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    setIsAnyCheckboxChecked(selectedApplications.length > 0);

    if (selectedApplications.length === 0) {
      setShowStatusDropdown(false);
    }
  }, [selectedApplications]);

  const handleStatusSelect = (status) => {
    setSelectedStatus(status);
    setShowConfirmationModal(true);
    setUpdateAll(false);
    setShowStatusDropdown(false);
  };

  const confirmStatusUpdate = () => {
    if (selectedApplications.length > 0 && selectedStatus) {
      updateApplicationStatus(
        {
          applicationIds: selectedApplications,
          status: selectedStatus,
          params: {
            update_all: updateAll,
            filters: filters,
            vacancy_id: selectedVacancy?.id,
          },
        },
        {
          onSuccess: () => {
            refetchApplications();
            showToast({
              message: `Status updated successfully.`,
              type: "success",
            });
            setShowConfirmationModal(false);
            setSelectedApplications([]);
          },
          onError: (error) => {
            showToast({
              message: error.message || "Failed to update status",
              type: "error",
            });
            setShowConfirmationModal(false);
          },
        },
      );
    } else {
      showToast({
        message: "No application selected",
        type: "error",
      });
      setShowConfirmationModal(false);
    }
  };

  const cancelStatusUpdate = () => {
    setShowConfirmationModal(false);
    setSelectedStatus(null);
  };

  useEffect(() => {
    const statuses = [...(selectedVacancy?.vacancy_statuses || [])]
      .sort(compareVacancyStatusOrder)
      .map((status_relation) => ({
        value: status_relation.id,
        label: status_relation.status.name,
      }));
    setStatusOptions(statuses);
    setSelectedApplications([]);
  }, [selectedVacancy]);

  // const handleUpdateStatus = (applicationId, newStatus) => {
  //   updateApplicationStatus(
  //     { applicationId, status: newStatus },
  //     {
  //       onSuccess: () => {
  //         refetchApplications();
  //             showToast({
  //           message: "Application status updated successfully",
  //           type: "success"
  //         });
  //       },
  //       onError: (error) => {
  //         showToast({
  //           message: error.message || "Failed to update application status",
  //           type: "error"
  //         });
  //       },
  //     }
  //   );
  // };

  useEffect(() => {
    localStorage.setItem(
      "selectedApplications",
      JSON.stringify(selectedApplications),
    );
  }, [selectedApplications]);

  useEffect(() => {
    localStorage.setItem("currentPage", currentPage.toString());
  }, [currentPage]);

  useEffect(() => {
    localStorage.setItem("searchTerm", searchQuery);
  }, [searchQuery]);

  useEffect(() => {
    localStorage.setItem(sortConfigStorageKey, JSON.stringify(sortConfig));
  }, [sortConfig, sortConfigStorageKey]);

  const [showApplicationViewModal, setShowApplicationViewModal] =
    useState(null);
  const handleApplicationViewModalOpen = (applicant) => {
    applicant.vacancy = selectedVacancy;
    setShowApplicationViewModal(applicant);
  };
  const handleApplicationViewModalClose = () => {
    setShowApplicationViewModal(null);
  };

  const [filterInputs, setFilterInputs] = useState(null);

  const handleDownloadCSV = async (type) => {
    let applicationsToDownload = [];

    if (type === "selected") {
      applicationsToDownload = processedApplications.filter((app) =>
        selectedApplications.includes(app.id),
      );

      if (selectedApplications.length === 0) {
        // If no applications are selected, download all applications from all pages
        showToast({
          message: "Fetching all applications data...",
          type: "success",
        });

        try {
          // First, get the total count to know how many pages to fetch with current filters
          const firstPageData =
            await applicationService.getApplicationsByVacancyId(
              selectedVacancy.id,
              {
                per_page: 100, // Use a reasonable page size
                page: 1,
                search: searchQuery, // Use current search query
                customFilters: filters, // Use current filters
                sortBy: sortConfig?.key || undefined,
                sortOrder:
                  sortConfig?.direction === "ascending"
                    ? "asc"
                    : sortConfig?.direction === "descending"
                      ? "desc"
                      : undefined,
              },
            );

          if (firstPageData && firstPageData.pagination) {
            const totalPages = firstPageData.pagination.last_page;
            const totalApplications = firstPageData.pagination.total;

            // Fetch all pages silently with current filters
            let allApplications = [];
            for (let page = 1; page <= totalPages; page++) {
              const pageData =
                await applicationService.getApplicationsByVacancyId(
                  selectedVacancy.id,
                  {
                    per_page: 100,
                    page: page,
                    search: searchQuery, // Use current search query
                    customFilters: filters, // Use current filters
                    sortBy: sortConfig?.key || undefined,
                    sortOrder:
                      sortConfig?.direction === "ascending"
                        ? "asc"
                        : sortConfig?.direction === "descending"
                          ? "desc"
                          : undefined,
                  },
                );

              if (pageData && pageData.applications) {
                allApplications = [
                  ...allApplications,
                  ...pageData.applications,
                ];
              }
            }

            if (allApplications.length > 0) {
              // Process all applications similar to how we process current page
              const allEnrichedApplications = allApplications.map(
                (application) => {
                  const candidate = application.candidate || {};
                  const vacancy = selectedVacancy;
                  const resume = application.resume || "";

                  const fieldsData = {};
                  if (
                    application.application_fields &&
                    Array.isArray(application.application_fields)
                  ) {
                    application.application_fields.forEach((field) => {
                      fieldsData[field.field_name] = field.field_value;
                    });
                  }

                  const dateToDisplay = new Date(
                    application.updated_at || application.created_at,
                  );

                  const formattedDateTime = dateToDisplay.toLocaleString(
                    "en-US",
                    {
                      year: "numeric",
                      month: "numeric",
                      day: "numeric",
                      hour: "2-digit",
                      minute: "2-digit",
                      hour12: true,
                    },
                  );

                  let lastStatus = "";
                  const statusHistory =
                    application.application_status_history || [];
                  if (
                    application.status?.status?.name === "Rejected" &&
                    statusHistory.length > 1
                  ) {
                    const sortedHistory = statusHistory.sort(
                      (a, b) => new Date(b.changed_at) - new Date(a.changed_at),
                    );
                    const previousStatus = sortedHistory.find(
                      (status) =>
                        status.vacancy_status?.status?.name !== "Rejected",
                    );
                    lastStatus =
                      previousStatus?.vacancy_status?.status?.name || "";
                  }

                  return {
                    id: application.id,
                    candidateName: candidate?.name || "Unknown",
                    vacancyTitle: vacancy?.title || "Unknown Position",
                    rating: application?.total_score?.total_score || 0,
                    sms_score: Number(application?.sms_score) || 0,
                    currentStage: application.status?.status?.name || "",
                    lastStatus: lastStatus,
                    priority: application.priority || 0,
                    email: candidate?.email || "",
                    resume: resume,
                    vacancy_id: application.vacancy_id,
                    candidate_id: application.candidate_id,
                    appliedTime: dateToDisplay.toLocaleTimeString(),
                    appliedDateTime: formattedDateTime,
                    rawDateTime: dateToDisplay.getTime(),
                    updated_at: application.updated_at || application.created_at,
                    fields: fieldsData,
                    raw_application: application,
                    tags: application?.application_tags || [],
                  };
                },
              );

              applicationsToDownload = allEnrichedApplications;
            } else {
              // Fallback to current page if no applications found
              applicationsToDownload = processedApplications;
            }
          } else {
            // Fallback to current page if pagination info not available
            applicationsToDownload = processedApplications;
          }
        } catch (error) {
          console.error("Error fetching all applications:", error);
          // Fallback to current page if API call fails
          applicationsToDownload = processedApplications;
        }
      } else {
        applicationsToDownload = processedApplications.filter((app) =>
          selectedApplications.includes(app.id),
        );
      }
    } else {
      applicationsToDownload = processedApplications; // Download all applications
    }

    // Function to sanitize text by removing HTML tags
    const sanitizeText = (text) => {
      return text ? text.replace(/<[^>]+>/g, "") : "";
    };

    // Get all unique field names from the processed applications
    const fieldNames = applicationsToDownload.reduce((acc, application) => {
      if (application.fields) {
        Object.keys(application.fields).forEach((fieldName) => {
          if (!acc.includes(fieldName)) {
            acc.push(fieldName);
          }
        });
      }
      return acc;
    }, []);

    // Define the headers, including the dynamic field names
    const headers = [
      "Candidate Name",
      "Email",
      "Stage",
      "Last Status Before Rejection",
      "Rating",
      "Social Mobility Score",
      "Date Last Modified",
      "Priority",
      ...fieldNames,
    ];

    // Generate the rows, including the dynamic field values
    const rows = applicationsToDownload.map((application) => {
      const rejectedAt =
        application.currentStage === "Rejected"
          ? new Date().toLocaleString()
          : "";

      let lastStatusBeforeRejection = "";
      if (application.currentStage === "Rejected") {
        const last_status =
          application.raw_application.application_status_history?.sort(
            (a, b) => new Date(b.changed_at) - new Date(a.changed_at),
          )[1];
        lastStatusBeforeRejection = last_status
          ? last_status.vacancy_status.status.name
          : "";
      }

      const row = [
        sanitizeText(application.candidateName),
        sanitizeText(application.email),
        sanitizeText(application.currentStage),
        sanitizeText(lastStatusBeforeRejection),
        application.rating,
        application.sms_score,
        sanitizeText(application.appliedDateTime),
        application.priority,
        ...fieldNames.map((fieldName) =>
          sanitizeText(
            formatFieldDisplayValue(application.fields?.[fieldName]),
          ),
        ),
      ];

      return row;
    });

    // Function to properly escape and quote CSV fields
    const escapeCSVField = (field) => {
      // Convert to string and handle null/undefined
      const value = field === null || field === undefined ? "" : String(field);
      // Double up any quotes and wrap the entire field in quotes
      return `"${value.replace(/"/g, '""')}"`;
    };

    // Create the CSV content with properly escaped fields
    const csvContent = [
      headers.map(escapeCSVField).join(","),
      ...rows.map((row) => row.map(escapeCSVField).join(",")),
    ].join("\n");

    // Trigger the download
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = "applications.csv";
    link.click();

    // Show success message after download
    if (type === "selected" && selectedApplications.length === 0) {
      const hasFilters =
        Object.keys(filters).length > 0 || searchQuery.trim() !== "";
      if (hasFilters) {
        showToast({
          message: `Successfully downloaded ${applicationsToDownload.length} applications (with current filters)!`,
          type: "success",
        });
      } else {
        showToast({
          message: "Successfully downloaded all applications!",
          type: "success",
        });
      }
    } else if (type === "selected" && selectedApplications.length > 0) {
      showToast({
        message: `Successfully downloaded ${applicationsToDownload.length} selected applications!`,
        type: "success",
      });
    } else {
      showToast({
        message: `Successfully downloaded ${applicationsToDownload.length} applications!`,
        type: "success",
      });
    }
  };

  const handleCVPopupOpen = (
    url,
    candidateName,
    applicationId,
    application,
  ) => {
    if (url.endsWith(".doc") || url.endsWith(".docx")) {
      setCvUrl(
        `https://view.officeapps.live.com/op/view.aspx?src=${encodeURIComponent(
          url,
        )}`,
      );
    } else {
      setCvUrl(url);
    }
    setShowCVPopup(true);
    setCvCandidateName(candidateName);
    setCvApplicationId(applicationId);
  };

  const handleCVPopupClose = () => {
    setShowCVPopup(false);
    setCvUrl("");
  };

  const handleDecision = (decision, applicationId, currentStatus) => {
    if (!applicationId) {
      showToast({
        message: "No application selected",
        type: "error",
      });
      return;
    }

    // Log the selected application
    const selectedApplication = processedApplications.find(
      (app) => app.id === applicationId,
    );

    // Check if the candidate is already on the "Rejected" stage
    if (currentStatus?.status?.name === "Rejected" && decision === "rejected") {
      showToast({
        message: "Candidate is already on the Rejected stage.",
        type: "error",
      });
      return;
    }

    // Derive currentPOrder from currentStatus
    const currentPOrder = currentStatus?.p_order
      ? parseInt(currentStatus.p_order)
      : 0;

    let nextStatus;
    if (decision === "accepted") {
      // Find the next status with p_order greater than the current one
      nextStatus = selectedVacancy?.vacancy_statuses?.find(
        (status) => status.p_order > currentPOrder,
      );

      // If the next status is "Rejected", show a message and return
      if (nextStatus?.status?.name === "Rejected") {
        showToast({
          message:
            "Next status is Rejected. You cannot move to the next stage.",
          type: "error",
        });
        return;
      }
    } else {
      // Find the last status (highest p_order) for rejection
      nextStatus = selectedVacancy?.vacancy_statuses?.reduce((prev, curr) =>
        curr.p_order > prev.p_order ? curr : prev,
      );
    }

    if (!nextStatus) {
      showToast({
        message: "Applicant is on Rejected stage",
        type: "error",
      });
      console.error("No valid next status found.");
      return;
    }

    const statusId = nextStatus.id;

    updateApplicationStatus(
      { applicationIds: [applicationId], status: statusId },
      {
        onSuccess: () => {
          refetchApplications();
          showToast({
            message: "Status updated successfully.",
            type: "success",
          });
          setSelectedApplications([]);
          handleCVPopupClose();
        },
        onError: (error) => {
          showToast({
            message: error.message || "Failed to update status",
            type: "error",
          });
          console.error("Error updating status:", error);
        },
      },
    );
  };

  // Pagination changes
  const handlePageChange = (page) => {
    setServerPagination((prev) => ({
      ...prev,
      current_page: page,
    }));
  };

  // Handle pagination dropdown change
  const handlePaginationChange = (selectedOption) => {
    const newPerPage = selectedOption.value;
    setItemsPerPage(newPerPage);
    setPerPage("applications-table", newPerPage);
    setServerPagination((prev) => ({
      ...prev,
      per_page: newPerPage,
      current_page: 1, // Reset to first page when changing items per page
    }));
  };

  // Handle click outside to close the CV popup
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (cvPopupRef.current && !cvPopupRef.current.contains(event.target)) {
        setShowCVPopup(false);
      }
    };

    if (showCVPopup) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showCVPopup]);

  // Use the useDownloadCVs hook
  const { mutate: downloadCVs } = useDownloadCVs();

  // Function to handle CV download
  const handleDownloadCVs = (downloadNameFormat = "candidate_name") => {
    // Call the API to download CVs with vacancy ID and filters
    // If no applications selected, only send vacancy_id
    // If applications selected, send both application_ids and vacancy_id
    // Always include filters if they exist

    const _filters =
      Object.keys(filters).length > 0 ? Object.assign({}, filters) : {};
    if (sortConfig?.key) {
      _filters.sort_by = sortConfig?.key;
      _filters.sort_order =
        sortConfig?.direction === "ascending"
          ? "asc"
          : sortConfig?.direction === "descending"
            ? "desc"
            : undefined;
    }
    _filters.download_name_format = downloadNameFormat;

    // Keep download order aligned with the current table sort order
    // (not the order checkboxes were clicked).
    const selectedIdsSet = new Set(selectedApplications);
    const idsInCurrentTableOrder = filteredApplications
      .map((application) => application.id)
      .filter((id) => selectedIdsSet.has(id));
    const idsInCurrentTableOrderSet = new Set(idsInCurrentTableOrder);
    const remainingSelectedIds = selectedApplications.filter(
      (id) => !idsInCurrentTableOrderSet.has(id),
    );
    const downloadApplicationIds = [
      ...idsInCurrentTableOrder,
      ...remainingSelectedIds,
    ];

    downloadCVs(
      {
        ids: downloadApplicationIds,
        vacancyId: selectedVacancy?.id,
        filters: _filters,
      },
      {
        onSuccess: (data) => {
          showToast({
            message: data.message,
            type: "success",
          });
          // Handle the download logic here, e.g., trigger a file download
        },
        onError: (error) => {
          showToast({
            message: "Failed to download CVs: " + error.message,
            type: "error",
          });
        },
      },
    );
  };

  const [isGenerateSummaryLoading, setIsGenerateSummaryLoading] = useState(false);

  const handleGenerateSummary = async () => {
    if (isGenerateSummaryLoading) return;

    const _filters = Object.keys(filters).length > 0 ? Object.assign({}, filters) : {};
    if (sortConfig?.key) {
      _filters.sort_by = sortConfig?.key;
      _filters.sort_order =
        sortConfig?.direction === "ascending"
          ? "asc"
          : sortConfig?.direction === "descending"
            ? "desc"
            : undefined;
    }

    // Keep summary candidate order aligned with the current table sort order.
    const selectedIdsSet = new Set(selectedApplications);
    const idsInCurrentTableOrder = filteredApplications
      .map((application) => application.id)
      .filter((id) => selectedIdsSet.has(id));
    const idsInCurrentTableOrderSet = new Set(idsInCurrentTableOrder);
    const remainingSelectedIds = selectedApplications.filter(
      (id) => !idsInCurrentTableOrderSet.has(id),
    );
    const summaryApplicationIds = [
      ...idsInCurrentTableOrder,
      ...remainingSelectedIds,
    ];

    setIsGenerateSummaryLoading(true);
    showToast({ message: "Your request is being processed. You’ll receive an email notification when it’s ready.", type: "info" });
    try {
      const data = await applicationService.generateSummary(
        summaryApplicationIds,
        selectedVacancy?.id,
        _filters
      );
      showToast({
        message: data?.message || "Summary generated successfully!",
        type: "success"
      });
    } catch (error) {
      showToast({
        message: error?.message || "Failed to generate summary.",
        type: "error"
      });
    } finally {
      setIsGenerateSummaryLoading(false);
    }
  };

  // State to force ApplicationFilters to refresh
  const [filtersKey, setFiltersKey] = useState(0);

  // Add effect to handle initial status filter
  useEffect(() => {
    console.log("ApplicationTable received initial filters:", initialFilters);
    if (initialFilters) {
      Object.keys(initialFilters).forEach((filterKey) => {
        console.log(
          "Setting filter:",
          filterKey,
          "with value:",
          initialFilters[filterKey],
        );
        handleFilterChange(filterKey, initialFilters[filterKey]);
      });
      Object.keys(initialFilters).forEach((filterKey) => {
        console.log("Adding filter:", filterKey);
        addFilter(filterKey);
      });

      // Force ApplicationFilters component to refresh by updating key
      setFiltersKey((prev) => prev + 1);
    }
  }, [initialFilters]);

  // Add new state for timeout
  const [showNoDataMessage, setShowNoDataMessage] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailSubject, setEmailSubject] = useState("");
  const [emailContent, setEmailContent] = useState("");
  const [isSendingEmail, setIsSendingEmail] = useState(false);

  // Column order state for drag and drop
  const columnOrderStorageKey = `columnOrder_${selectedVacancy?.id || "vacancy"}`;

  // Drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  // Handle drag end for column reordering
  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (
      active?.id &&
      over?.id &&
      active.id !== over.id &&
      columnOrder?.length > 0
    ) {
      const oldIndex = columnOrder.findIndex((id) => id === active.id);
      const newIndex = columnOrder.findIndex((id) => id === over.id);

      if (oldIndex !== -1 && newIndex !== -1) {
        const newOrder = arrayMove(columnOrder, oldIndex, newIndex);
        setColumnOrder(newOrder);
        localStorage.setItem(columnOrderStorageKey, JSON.stringify(newOrder));
      }
    }
  };

  // Initialize column order when vacancy changes
  useEffect(() => {
    if (selectedVacancy?.id && columns.length > 0) {
      const saved = localStorage.getItem(columnOrderStorageKey);
      if (saved) {
        try {
          const savedOrder = JSON.parse(saved);
          // Validate that saved order contains valid column keys
          const validOrder = savedOrder.filter((key) =>
            columns.some((col) => col.key === key),
          );

          // Add any new columns that aren't in the saved order (excluding actions)
          const allColumnKeys = columns
            .map((col) => col.key)
            .filter((key) => key !== "actions");
          const newColumns = allColumnKeys.filter(
            (key) => !validOrder.includes(key),
          );
          const completeOrder = [...validOrder, ...newColumns];

          setColumnOrder(completeOrder);

          // Update localStorage with the complete order if there were new columns
          if (newColumns.length > 0) {
            localStorage.setItem(
              columnOrderStorageKey,
              JSON.stringify(completeOrder),
            );
          }
        } catch (e) {
          // If parsing fails, initialize with default order (excluding actions)
          const defaultOrder = columns
            .map((col) => col.key)
            .filter((key) => key !== "actions");
          setColumnOrder(defaultOrder);
        }
      } else {
        // If no saved order, initialize with default order (excluding actions)
        const defaultOrder = columns
          .map((col) => col.key)
          .filter((key) => key !== "actions");
        setColumnOrder(defaultOrder);
      }
    }
  }, [selectedVacancy?.id, columnOrderStorageKey, columns.length]);

  // Handle column changes (like new custom fields being added)
  useEffect(() => {
    if (columns.length > 0 && columnOrder.length > 0) {
      const allColumnKeys = columns
        .map((col) => col.key)
        .filter((key) => key !== "actions");
      const newColumns = allColumnKeys.filter(
        (key) => !columnOrder.includes(key),
      );

      if (newColumns.length > 0) {
        const updatedOrder = [...columnOrder, ...newColumns];
        setColumnOrder(updatedOrder);
        localStorage.setItem(
          columnOrderStorageKey,
          JSON.stringify(updatedOrder),
        );
      }

      // Remove any columns that no longer exist (excluding actions)
      const validOrder = columnOrder.filter(
        (key) => columns.some((col) => col.key === key) && key !== "actions",
      );

      if (validOrder.length !== columnOrder.length) {
        setColumnOrder(validOrder);
        localStorage.setItem(columnOrderStorageKey, JSON.stringify(validOrder));
      }
    }
  }, [columns, columnOrder, columnOrderStorageKey]);

  // Add useEffect for timeout
  useEffect(() => {
    if (filteredApplications.length === 0) {
      setShowNoDataMessage(false);
      const timer = setTimeout(() => {
        setShowNoDataMessage(true);
      }, 4000);
      return () => clearTimeout(timer);
    }
  }, [filteredApplications.length]);

  return (
    <div className="w-full">
      {/* Update Toaster component with custom configuration */}

      {/* Email Modal */}
      {showEmailModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <div
              className="fixed inset-0 bg-foreground/40 transition-opacity"
              onClick={() => setShowEmailModal(false)}
            ></div>
            <div className="relative transform overflow-hidden rounded-lg bg-card text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-3xl">
              <div className="bg-card px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mt-3 text-center sm:mt-0 sm:text-left w-full">
                    <div className="flex justify-between items-center mb-4">
                      <h3 className="text-lg font-medium text-foreground">
                        Send Email to {selectedApplications.length} candidate
                        {selectedApplications.length > 1 ? "s" : ""}
                      </h3>
                      <button
                        onClick={() => setShowEmailModal(false)}
                        className="text-muted-foreground hover:text-muted-foreground"
                      >
                        <XIcon className="h-6 w-6" />
                      </button>
                    </div>

                    <div className="mt-4">
                      <label
                        htmlFor="email-subject"
                        className="block text-sm font-medium text-foreground mb-2"
                      >
                        Subject
                      </label>
                      <input
                        type="text"
                        id="email-subject"
                        value={emailSubject}
                        onChange={(e) => setEmailSubject(e.target.value)}
                        className="w-full px-3 py-2 border border-input rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary"
                        placeholder="Enter email subject..."
                      />
                    </div>

                    <div className="mt-4">
                      <label className="block text-sm font-medium text-foreground mb-2">
                        Email Content
                      </label>
                      <Editor
                        apiKey={import.meta.env.VITE_TINYMCE_API_KEY}
                        value={emailContent}
                        onEditorChange={(content) => setEmailContent(content)}
                        init={{
                          height: 400,
                          menubar: false,
                          plugins: [
                            "advlist",
                            "autolink",
                            "lists",
                            "link",
                            "image",
                            "charmap",
                            "preview",
                            "anchor",
                            "searchreplace",
                            "visualblocks",
                            "code",
                            "fullscreen",
                            "insertdatetime",
                            "media",
                            "table",
                            "code",
                            "help",
                            "wordcount",
                          ],
                          toolbar:
                            "undo redo | blocks | " +
                            "bold italic forecolor | alignleft aligncenter " +
                            "alignright alignjustify | bullist numlist outdent indent | " +
                            "link | removeformat | help",
                          content_style:
                            "body { font-family: -apple-system, BlinkMacSystemFont, San Francisco, Segoe UI, Roboto, Helvetica Neue, sans-serif; font-size: 14px }",
                          skin: "oxide",
                          content_css: "default",
                          branding: false,
                          statusbar: true,
                          resize: true,
                          promotion: false,
                        }}
                      />
                    </div>
                    <div className="mt-4 p-3 bg-muted rounded-lg w-fit border border-border shadow-sm">
                      <div className="space-y-2 text-sm">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-[#0c4f88]">
                            Name:
                          </span>
                          <span className="text-foreground font-semibold">
                            &#123;$name&#125;
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-[#0c4f88]">
                            Job Title:
                          </span>
                          <span className="text-foreground font-semibold">
                            &#123;$job_title&#125;
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-[#0c4f88]">
                            Company:
                          </span>
                          <span className="text-foreground font-semibold">
                            &#123;$company_name&#125;
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-muted px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
                <button
                  type="button"
                  disabled={isSendingEmail}
                  onClick={async () => {
                    if (!emailSubject.trim()) {
                      showToast({
                        message: "Please enter an email subject",
                        type: "error",
                      });
                      return;
                    }
                    if (
                      !emailContent.trim() ||
                      emailContent.trim() === "<p><br></p>"
                    ) {
                      showToast({
                        message: "Please enter email content",
                        type: "error",
                      });
                      return;
                    }

                    setIsSendingEmail(true);

                    try {
                      // Get candidate IDs and application IDs from selected applications
                      const emailPayload = {
                        application_ids: selectedApplications,
                        subject: emailSubject.trim(),
                        body: emailContent.trim(),
                      };

                      // Send email using the service
                      await emailService.sendEmailToCandidates(emailPayload);

                      setShowEmailModal(false);
                      showToast({
                        message: `Email sent successfully to ${selectedApplications.length} candidate${selectedApplications.length > 1 ? "s" : ""}!`,
                        type: "success",
                      });

                      // Clear selected checkboxes and reset email fields
                      setSelectedApplications([]);
                      setIsAnyCheckboxChecked(false);
                      localStorage.setItem(
                        "selectedApplications",
                        JSON.stringify([]),
                      );
                      setEmailSubject("");
                      setEmailContent("");
                    } catch (error) {
                      showToast({
                        message: error.message || "Failed to send email",
                        type: "error",
                      });
                    } finally {
                      setIsSendingEmail(false);
                    }
                  }}
                  className="inline-flex w-full justify-center rounded-md bg-primary-dark hover:scale-110 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary-dark sm:ml-3 sm:w-auto disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Send Email
                </button>
                <button
                  type="button"
                  onClick={() => setShowEmailModal(false)}
                  className="mt-3 inline-flex w-full justify-center rounded-md bg-card px-3 py-2 text-sm font-semibold text-foreground shadow-sm ring-1 ring-inset ring-input hover:bg-muted sm:mt-0 sm:w-auto"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Modal */}
      {showConfirmationModal && (
        <div
          className="relative z-20"
          aria-labelledby="modal-title"
          role="dialog"
          aria-modal="true"
        >
          <div
            className="fixed inset-0 bg-foreground/40 transition-opacity"
            aria-hidden="true"
          ></div>
          <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
            <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
              <div className="relative transform overflow-hidden rounded-lg bg-card text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg">
                <div className="bg-card px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <div className="sm:flex sm:items-start">
                    <div className="mx-auto flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-green-100 sm:mx-0 sm:h-10 sm:w-10">
                      <svg
                        className="h-6 w-6 text-green-600"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth="1.5"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                    </div>
                    <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                      <h3
                        className="text-base font-semibold text-foreground"
                        id="modal-title"
                      >
                        Confirm Status Update
                      </h3>
                      <div className="mt-2">
                        <div className="flex flex-col gap-2 mb-3">
                          <label className="inline-flex items-center">
                            <input
                              type="radio"
                              name="update_scope"
                              value="selected"
                              checked={!updateAll}
                              onChange={() => setUpdateAll(false)}
                              className="h-4 w-4 text-primary border-input focus:ring-primary"
                            />
                            <span className="ml-2 text-sm text-foreground">
                              Apply to selected only
                              {selectedApplications &&
                                selectedApplications.length > 0 ? (
                                <>
                                  <span className="ml-1 text-xs text-muted-foreground">
                                    ({selectedApplications.length} selected)
                                  </span>
                                </>
                              ) : (
                                <span className="ml-2 text-xs text-muted-foreground">
                                  (0 selected)
                                </span>
                              )}
                            </span>
                          </label>
                          {selectedApplications.length > 1 && (
                            <label className="inline-flex items-center">
                              <input
                                type="radio"
                                name="update_scope"
                                value="all"
                                checked={updateAll}
                                onChange={() => setUpdateAll(true)}
                                className="h-4 w-4 text-primary border-input focus:ring-primary"
                              />
                              <span className="ml-2 text-sm text-foreground">
                                Apply to all using same filters
                              </span>
                            </label>
                          )}
                        </div>
                        {/* <p className="text-sm text-muted-foreground">
                          Are you sure you want to move{" "}
                          {selectedApplications.length} candidate
                          {selectedApplications.length > 1 ? "s" : ""} to{" "}
                          {statusOptions.find(
                            (option) => option.value === selectedStatus
                          )?.label || "new status"}
                          ? or move all candidates ?
                        </p> */}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="bg-muted px-4 py-3 sm:flex sm:px-6 justify-end">
                  <button
                    type="button"
                    onClick={cancelStatusUpdate}
                    disabled={isUpdatingStatus}
                    className="mt-3 inline-flex w-full justify-center rounded-md bg-card px-3 py-2 text-sm font-semibold text-foreground ring-1 shadow-xs ring-input ring-inset hover:bg-muted sm:mt-0 sm:w-auto disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={confirmStatusUpdate}
                    disabled={isUpdatingStatus}
                    className="inline-flex w-full justify-center rounded-md bg-primary-dark hover:scale-110 px-3 py-2 text-sm font-semibold text-white shadow-xs hover:bg-primary-dark sm:ml-3 sm:w-auto disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isUpdatingStatus ? (
                      <div className="flex items-center gap-2">
                        <Loader2 className="w-4 h-4 animate-spin" />
                        <span>Updating...</span>
                      </div>
                    ) : (
                      "Confirm"
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {showCVPopup && (
        <div className="fixed inset-0 flex items-center justify-center z-[99] bg-black bg-opacity-75">
          <div
            ref={cvPopupRef}
            className="bg-card rounded-lg shadow-lg w-[90%] h-[95vh] max-w-[1200px] max-h-[95vh] relative min-w-[85%] min-h-[85%]"
          >
            <div className="flex justify-between items-center p-4 border-b">
              <h3 className="text-lg font-semibold">{cvCandidateName} - CV</h3>
              <button
                onClick={handleCVPopupClose}
                className="text-muted-foreground hover:text-foreground"
              >
                <XIcon className="h-6 w-6" />
              </button>
            </div>
            <div className="p-4 h-[calc(100%-56px)]">
              <iframe
                src={cvUrl}
                className="w-full h-full border rounded-lg"
                title="CV Viewer"
              ></iframe>
            </div>
            <div className="fixed bottom-8 left-1/2 transform -translate-x-1/2 flex flex-col items-center">
              <div className="flex items-center gap-8 mb-4">
                <button
                  onClick={() => {
                    const selectedApplication = processedApplications.find(
                      (app) => app.id === cvApplicationId,
                    );
                    const currentStatus =
                      selectedApplication?.raw_application?.status;
                    handleDecision("rejected", cvApplicationId, currentStatus);
                  }}
                  className="bg-red-500 text-white p-4 rounded-full shadow-lg hover:bg-red-600 transition-colors hover:scale-110 transform duration-200"
                  title="Reject (Left Arrow)"
                >
                  <ThumbsDown className="w-8 h-8" />
                </button>

                <button
                  onClick={() => {
                    const selectedApplication = processedApplications.find(
                      (app) => app.id === cvApplicationId,
                    );
                    const currentStatus =
                      selectedApplication?.raw_application?.status;
                    handleDecision("accepted", cvApplicationId, currentStatus);
                  }}
                  className="bg-primary text-white p-4 rounded-full shadow-lg hover:bg-primary-dark transition-colors hover:scale-110 transform duration-200"
                  title="Accept (Right Arrow)"
                >
                  <ThumbsUp className="w-8 h-8" />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {!selectedVacancy ? (
        <div className="w-full p-10 text-center">
          <div className="text-muted-foreground mb-4">
            <GalleryVerticalEnd className="h-14 w-14 mx-auto" />
          </div>
          <p className="text-muted-foreground text-lg font-medium">
            Please select a vacancy to view applications table
          </p>
        </div>
      ) : isLoading ? (
        <div className="flex justify-center items-center h-64 bg-card p-8 rounded-lg shadow-md">
          <Loader bgColor="bg-transparent" />
        </div>
      ) : (
        <>
          <div className="mb-3 flex flex-col gap-3 md:flex-row md:items-center md:justify-between md:gap-4">
            <div className="relative w-full min-w-0 md:flex-1 md:max-w-3xl">
              <span className="pointer-events-none absolute flex items-center left-3 top-1/2 -translate-y-1/2">
                <Search className="h-4 w-4 text-muted-foreground" />
              </span>
              <input
                id="search"
                type="text"
                placeholder="Search candidates..."
                value={tempSearchTerm}
                onChange={(e) => handleSearchChange(e.target.value)}
                className="border border-input bg-background text-foreground placeholder:text-muted-foreground rounded-lg pl-10 pr-4 w-full min-w-0 h-[38px] text-sm outline-none focus:ring-1 focus:ring-ring"
              />
            </div>

            <div className="flex flex-wrap items-center gap-2 shrink-0 md:justify-end">
              {activeFilters.length > 0 && (
                <button
                  onClick={() => {
                    // Create URL search parameters from current filters
                    const searchParams = new URLSearchParams();

                    // Add each filter as a search parameter
                    Object.entries(filters).forEach(([key, value]) => {
                      if (
                        value !== null &&
                        value !== undefined &&
                        value !== ""
                      ) {
                        // Handle array values (for multi-select filters)
                        if (Array.isArray(value)) {
                          value.forEach((item) =>
                            searchParams.append(key, item),
                          );
                        } else {
                          searchParams.append(key, value);
                        }
                      }
                    });

                    // Add search query if it exists
                    if (searchQuery && searchQuery.trim() !== "") {
                      searchParams.append("search", searchQuery.trim());
                    }

                    // Add application IDs if applications are selected
                    if (selectedApplications.length >= 1) {
                      // Add each selected application ID to search params
                      selectedApplications.forEach((applicationId) => {
                        searchParams.append("application_id", applicationId);
                      });
                    }

                    // Construct the URL with filters
                    const url = `/vacancies/${selectedVacancy.id}/pre-screen?${searchParams.toString()}`;

                    // Open in new tab
                    window.open(url, "_blank");
                  }}
                  className="flex items-center space-x-1 px-3 h-[38px] bg-primary-dark text-white rounded-lg hover:bg-opacity-90 transition-all shadow-sm text-sm font-semibold whitespace-nowrap"
                >
                  <Eye className="h-4 w-4" />
                  <span>Pre-Screen</span>
                </button>
              )}
              {isAnyCheckboxChecked && (
                <div className="relative" ref={statusDropdownRef}>
                  <button
                    onClick={() => setShowStatusDropdown(!showStatusDropdown)}
                    className="flex items-center space-x-1 px-3 h-[38px] bg-primary-dark text-white rounded-lg hover:bg-opacity-90 transition-all shadow-sm text-sm font-semibold"
                  >
                    <span>Status</span>
                    <ChevronDown className="h-4 w-4" />
                  </button>

                  {showStatusDropdown && (
                    <div className="absolute left-0 top-12 mt-2 bg-card shadow-lg rounded-md border p-3 min-w-[200px] max-w-[300px] z-10">
                      <div className="font-medium pb-2 border-b mb-2">
                        Move To
                      </div>
                      {statusOptions.map((option) => (
                        <div
                          key={option.value}
                          onClick={(e) => {
                            e.stopPropagation(); // Prevent event bubbling
                            handleStatusSelect(option.value);
                            // Don't close the dropdown here
                          }}
                          className="cursor-pointer py-1 hover:bg-muted px-2 rounded"
                        >
                          {option.label}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
              {isAnyCheckboxChecked && (
                <button
                  onClick={() => setShowEmailModal(true)}
                  className="flex items-center space-x-1 px-3 h-[38px] bg-primary-dark text-white rounded-lg hover:bg-opacity-90 transition-all shadow-sm text-sm font-semibold"
                >
                  <span>Send Email</span>
                </button>
              )}
              <ApplicationFilters
                key={filtersKey}
                columns={columns}
                visibleColumns={visibleColumns}
                // processedApplications={[]}
                filtersStorageKey={filtersStorageKey}
                activeFiltersStorageKey={activeFiltersStorageKey}
                onFiltersChange={(filters, activeFilters) => {
                  setFilters(filters);
                  setActiveFilters(activeFilters);
                }}
                getFiltersComponent={(_filterInputs) =>
                  setFilterInputs(_filterInputs)
                }
              />
              <div className="relative" ref={columnToggleRef}>
                <button
                  className="flex items-center space-x-1 px-3 h-[38px] bg-primary-dark text-white rounded-lg hover:bg-opacity-90 transition-all shadow-sm text-sm font-semibold"
                  onClick={() => setShowColumnToggle(!showColumnToggle)}
                >
                  <Settings className="h-4 w-4" />
                  <span>Columns</span>
                </button>

                {showColumnToggle && (
                  <div className="absolute right-0 top-12 z-10 mt-2 bg-card shadow-lg rounded-md border p-3 min-w-[200px] max-w-[300px]">
                    <div className="font-medium pb-2 border-b mb-2">
                      Toggle Columns
                    </div>
                    {columns
                      .filter(
                        (column) =>
                          column.key !== "candidateName" &&
                          column.key !== "actions" &&
                          column.key !== "selection",
                      )
                      .map((column) => (
                        <div
                          key={column.key}
                          className="flex items-center justify-between py-1 px-2 hover:bg-muted rounded-md"
                          onClick={(e) => {
                            // Only toggle if the click is not on the checkbox
                            if (e.target.type !== "checkbox") {
                              e.preventDefault();
                              toggleColumnVisibility(column.key);
                            }
                          }}
                        >
                          <label
                            htmlFor={`toggle-${column.key}`}
                            className="cursor-pointer w-full"
                          >
                            {column.label}
                          </label>
                          <input
                            type="checkbox"
                            id={`toggle-${column.key}`}
                            checked={visibleColumns[column.key]}
                            onChange={() => toggleColumnVisibility(column.key)}
                            className="ml-2 cursor-pointer"
                          />
                        </div>
                      ))}
                  </div>
                )}
              </div>
              <div className="relative" ref={downloadDropdownRef}>
                <button
                  onClick={() => setShowDownloadDropdown(!showDownloadDropdown)}
                  className="flex items-center space-x-1 px-3 h-[38px] bg-primary-dark text-white rounded-lg hover:bg-opacity-90 transition-all shadow-sm text-sm font-semibold"
                >
                  <span>Download</span>
                  <ChevronDown className="h-4 w-4" />
                </button>
                {showDownloadDropdown && (
                  <div className="absolute right-0 mt-2 w-48 bg-card shadow-lg rounded-md border max-w-[300px] p-2 z-10">
                    <div
                      onClick={() => {
                        handleDownloadCSV("selected");
                        setShowDownloadDropdown(false);
                      }}
                      className="cursor-pointer py-1 hover:bg-muted px-2 rounded"
                    >
                      {selectedApplications.length > 0
                        ? `Download CSV (${selectedApplications.length} selected)`
                        : "Download CSV (All Pages)"}
                    </div>

                    <div
                      onClick={() => {
                        setShowDownloadCVDialog(true);
                        setShowDownloadDropdown(false);
                      }}
                      className="cursor-pointer py-1 hover:bg-muted px-2 rounded"
                    >
                      Download CVs
                    </div>

                    <div
                      onClick={() => handleGenerateSummary()}
                      className={`py-1 px-2 rounded flex items-center gap-2 whitespace-nowrap ${
                        isGenerateSummaryLoading
                          ? "pointer-events-none"
                          : "cursor-pointer hover:bg-muted"
                      }`}
                    >
                      {isGenerateSummaryLoading ? (
                        <>
                          Generating
                          <Loader2 className="h-4 w-4 animate-spin shrink-0" />
                        </>
                      ) : (
                        "Generate Summary"
                      )}
                    </div>


                  </div>
                )}
              </div>
            </div>
          </div>

          {filterInputs && filterInputs != null && activeFilters.length > 0 && (
            <div className="flex flex-wrap justify-end my-3 gap-2 w-full">
              {filterInputs}
            </div>
          )}

          {isApplicationsLoading ? (
            <div className="flex justify-center items-center h-64">
              <Loader bgColor="bg-transparent" />
            </div>
          ) : (
            <>
              {!applications?.applications?.length ? (
                <div className="text-center py-4 text-red-600 bg-red-200 rounded-lg">
                  <div className=" mb-2">
                    <FileSearch className="h-12 w-12 mx-auto" />
                  </div>
                  <p className="text-sm">
                    No applications found for the selected vacancy.
                  </p>
                </div>
              ) : (
                <>
                  <div className="overflow-x-auto bg-card rounded-lg shadow">
                    {isLoading ? (
                      <div className="flex justify-center items-center h-64">
                        <Loader bgColor="bg-transparent" />
                      </div>
                    ) : (
                      <>
                        <div className="hidden md:block">
                          <DndContext
                            sensors={sensors}
                            collisionDetection={closestCenter}
                            onDragEnd={handleDragEnd}
                          >
                            <table className="min-w-full divide-y divide-border">
                              <thead className="bg-muted ">
                                <tr className="border-b border-stroke dark:border-dark-3">
                                  {visibleColumnsArray.length > 0 ? (
                                    <SortableContext
                                      items={visibleColumnsArray.map(
                                        (col) => col.key,
                                      )}
                                      strategy={horizontalListSortingStrategy}
                                    >
                                      {visibleColumnsArray.map(
                                        (column, index) => (
                                          <SortableTableHeader
                                            key={column.key}
                                            column={column}
                                            index={index}
                                            sortConfig={sortConfig}
                                            handleSort={handleSort}
                                            visibleColumns={visibleColumns}
                                            selectedApplications={
                                              selectedApplications
                                            }
                                            filteredApplications={
                                              filteredApplications
                                            }
                                            handleSelectAll={handleSelectAll}
                                          />
                                        ),
                                      )}
                                    </SortableContext>
                                  ) : (
                                    <th className="px-4 py-4">
                                      Loading columns...
                                    </th>
                                  )}
                                </tr>
                              </thead>
                              <tbody className="bg-card divide-y divide-border">
                                {filteredApplications.length === 0 ? (
                                  <tr>
                                    <td
                                      colSpan={Object.keys(visibleColumns).length}
                                      className="text-center py-4"
                                    >
                                      {!showNoDataMessage ? (
                                        <div className="flex justify-center items-center h-64">
                                          <Loader bgColor="bg-transparent" />
                                        </div>
                                      ) : (
                                        <div className="text-center py-4 text-red-600">
                                          <div className="mb-2">
                                            <FileSearch className="h-12 w-12 mx-auto" />
                                          </div>
                                          <p className="text-sm">
                                            No applications found for the
                                            selected filter.
                                          </p>
                                        </div>
                                      )}
                                    </td>
                                  </tr>
                                ) : (
                                  filteredApplications.map((application) => (
                                    <tr
                                      key={application.id}
                                      className="hover:bg-muted"
                                    >
                                      {visibleColumnsArray.map((column) => (
                                        <td
                                          key={column.key}
                                          className={getApplicationTableCellClass(
                                            column,
                                          )}
                                        >
                                          {renderApplicationCellInner(
                                            column,
                                            application,
                                          )}
                                        </td>
                                      ))}
                                    </tr>
                                  ))
                                )}
                              </tbody>
                            </table>
                          </DndContext>
                        </div>

                        <div className="md:hidden divide-y divide-border">
                          {filteredApplications.length === 0 ? (
                            <div className="text-center py-8 px-4">
                              {!showNoDataMessage ? (
                                <div className="flex justify-center items-center h-48">
                                  <Loader bgColor="bg-transparent" />
                                </div>
                              ) : (
                                <div className="text-center py-4 text-red-600">
                                  <div className="mb-2">
                                    <FileSearch className="h-12 w-12 mx-auto" />
                                  </div>
                                  <p className="text-sm">
                                    No applications found for the selected
                                    filter.
                                  </p>
                                </div>
                              )}
                            </div>
                          ) : (
                            filteredApplications.map((application) => {
                              const headerColumns = visibleColumnsArray.filter(
                                (c) =>
                                  c.key === "selection" ||
                                  c.key === "name" ||
                                  c.key === "actions",
                              );
                              const bodyColumns = visibleColumnsArray.filter(
                                (c) =>
                                  c.key !== "selection" &&
                                  c.key !== "name" &&
                                  c.key !== "actions",
                              );
                              return (
                                <article
                                  key={application.id}
                                  className="px-4 py-4 bg-card"
                                >
                                  {headerColumns.length > 0 && (
                                    <div className="flex items-start gap-3 pb-3 mb-3 border-b border-border">
                                      {headerColumns.map((column) => (
                                        <div
                                          key={column.key}
                                          className={
                                            column.key === "name"
                                              ? "min-w-0 flex-1"
                                              : "shrink-0 pt-0.5"
                                          }
                                        >
                                          {renderApplicationCellInner(
                                            column,
                                            application,
                                          )}
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                  <div className="space-y-2.5">
                                    {bodyColumns.map((column) => {
                                      const inner =
                                        renderApplicationCellInner(
                                          column,
                                          application,
                                        );
                                      if (inner == null) {
                                        return null;
                                      }
                                      return (
                                        <div
                                          key={column.key}
                                          className="flex gap-3 items-start"
                                        >
                                          <span className="text-xs font-medium text-muted-foreground w-[40%] shrink-0 leading-snug pt-0.5">
                                            {column.label}
                                          </span>
                                          <div className="text-sm text-foreground flex-1 text-right min-w-0">
                                            {inner}
                                          </div>
                                        </div>
                                      );
                                    })}
                                  </div>
                                </article>
                              );
                            })
                          )}
                        </div>
                      </>
                    )}
                  </div>

                  {/* Pagination Controls */}
                  <Pagination
                    currentPage={serverPagination?.current_page}
                    totalPages={serverPagination?.last_page}
                    per_page={serverPagination?.per_page}
                    onPerPageChange={handlePaginationChange}
                    onPageChange={handlePageChange}
                  />
                </>
              )}
            </>
          )}
        </>
      )}
      {showApplicationViewModal && (
        <div className="fixed inset-0 flex items-center justify-center z-[99] bg-black bg-opacity-75">
          <div className="bg-card rounded-lg shadow-lg p-6 w-[40%] relative">
            <h3 className="text-lg font-semibold mb-4">Applicant Details</h3>
            <div className="max-h-[60vh] overflow-y-auto">
              <ApplicationView applicants={[showApplicationViewModal]} />
            </div>
            <div className="flex justify-end mt-4">
              <button
                onClick={handleApplicationViewModalClose}
                className="px-4 py-2 text-sm font-medium text-foreground bg-muted rounded-lg hover:bg-muted"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      <DialogBox
        isOpen={showDownloadCVDialog}
        onClose={() => setShowDownloadCVDialog(false)}
        title="Download CVs"
        showIcon={false}
        className="w-[92%] max-w-[460px] max-md:w-[92%]"
      >
     
        <div className="flex flex-col  mt-2">
          <button
            type="button"
            onClick={() => {
              handleDownloadCVs("candidate_name");
              setShowDownloadCVDialog(false);
            }}
            className="w-full rounded-lg bg-primary-dark text-white px-4 py-2 text-sm font-semibold hover:bg-opacity-90 transition-all"
          >
            Download with  fullName
          </button>
         
        </div>
      </DialogBox>
    </div>
  );
};

export default ApplicationTable;
